import { PhoneBankingService } from "@/modules/application/common/enum";
import type {
  IPhoneBankingApplicationForm,
  IPhoneBankingApplicationRequest,
  TPhoneBankingService,
} from "@/modules/application/common/types";
import {
  useCreatePhoneBankingApplicationMutation,
  useGetPhoneBankingApplicationQuery,
  useUpdatePhoneBankingApplicationMutation
} from "@/modules/application/services";
import { INPUT_MAX_LIMIT } from "@/shared/common/constants";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { InfoContainer } from "@/shared/components";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import { useHasFormChanges } from "@/shared/hooks";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

import { ApplicationTermsAndConditionsLink } from "../ApplicationTermsAndConditionsLink";

export const PhoneBankingApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, trigger, watch, reset } = useFormProps();

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    data: phoneBankingApplication,
    isLoading: isGetApplicationLoading,
  } = useGetPhoneBankingApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createPhoneBankingApplicationApi,
    { isLoading: isCreateLoading }
  ] = useCreatePhoneBankingApplicationMutation();

  const [
    updatePhoneBankingApplicationApi,
    { isLoading: isUpdateLoading }
  ] = useUpdatePhoneBankingApplicationMutation();

  const phoneBankingApplicationAction = useCallback((values: IPhoneBankingApplicationForm) => {
    const services: TPhoneBankingService[] = [];

    if (values.isReceiveInformation) {
      services.push(PhoneBankingService.ReceiveInformation);
    }

    if (values.isDefineExchangeRate) {
      services.push(PhoneBankingService.DefineExchangeRate);
    }

    const requestData: IPhoneBankingApplicationRequest = {
      applicationNumber: values.applicationNumber,
      purpose: values.purpose,
      services,
    };
    return isEdit ? updatePhoneBankingApplicationApi({
        data: requestData,
        id: `${phoneBankingApplication?.id}`,
      }).unwrap() :
      createPhoneBankingApplicationApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [phoneBankingApplication]);

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      isReceiveInformation: phoneBankingApplication?.services?.includes(PhoneBankingService.ReceiveInformation),
      isDefineExchangeRate: phoneBankingApplication?.services?.includes(PhoneBankingService.DefineExchangeRate),
      purpose: phoneBankingApplication?.purpose,
      applicationNumber: isEdit ?
        phoneBankingApplication?.applicationNumber : values.applicationNumber,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    isReceiveInformation: values.isReceiveInformation,
    isDefineExchangeRate: values.isDefineExchangeRate,
    purpose: values.purpose,
    applicationNumber: values.applicationNumber,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!phoneBankingApplication?.id) {
      setDefaultValues();
    }
  }, [phoneBankingApplication]);

  return (
    <ApplicationLayout
      title={t("application.phoneBanking.title")}
      page="Phone banking application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={phoneBankingApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="isReceiveInformation"
                className="mt-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    onClick={() => {
                      if (errors?.isDefineExchangeRate || errors?.isReceiveInformation) {
                        trigger?.(["isDefineExchangeRate"]);
                      }
                    }}
                    isInvalid={!!errors.isDefineExchangeRate}
                    label={t("application.phoneBanking.receiveInformationLabel")}
                  />
                )}
              />
              <FormField
                name="isDefineExchangeRate"
                className="mt-12"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    isInvalid={!!errors.isDefineExchangeRate}
                    label={t("application.phoneBanking.defineExchangeRateLabel")}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-20 mt-40"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <InfoContainer
                className="mb-24"
                content={t("application.phoneBanking.smsPasswordToPhoneNumber")}
              />
              <FormField
                name="isAgreeWithConditions"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    isInvalid={!!errors.isAgreeWithConditions}
                    label={<ApplicationTermsAndConditionsLink/>}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
